/* eslint-disable no-unused-vars */
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import clsx from 'clsx';
import {
  Box,
  Button,
  Card,
  Grid,
  TextField,
  InputAdornment,
  SvgIcon,
  Typography,
  makeStyles,
  LinearProgress,
  debounce,
  AccordionSummary,
  Accordion,
  AccordionDetails,
  Divider,
  Chip,
  colors
} from '@material-ui/core';
import { Search as SearchIcon } from 'react-feather';
import { CustomInputEvent, CustomKeyboardEvent } from 'src/types';
import { slices, useAppDispatch, useAppSelector } from 'src/redux';

import { CategoryListDropdown } from 'src/components/filters/CategoryDropdown';
import { useNavigate } from 'react-router';
import { usePermissions, useRoles, useSnackBar } from 'src/hooks';
import { Autocomplete } from '@material-ui/lab';
import SettingsIcon from '@material-ui/icons/Settings';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { DatePickerRangeComponent } from 'src/components';
import { multiBranchFeat } from 'src/constants/feature-toggle';
import { BranchListDropDown } from 'src/components/dropdown';
import { useBranchInfo } from 'src/hooks/branch/use-branch-info';
import { get, isEmpty } from 'lodash';
import { Supplier } from 'src/types/supplier';
import { unwrapResult } from '@reduxjs/toolkit';
import { dateToday } from 'src/utils';
import { date } from 'yup';
import {
  GetPurchaseOrdersPayload,
  PurchaseOrderStatusOption
} from 'src/redux/slices/purchase-order/types';
import PurchaseOrderStatusField from './PurchaseOrderStatusSelect';
import LocalStorageService from 'src/services/LocalStorageService';
import {
  purchaseOrderRoles,
  roles,
  searchFilter
} from 'src/redux/slices/purchase-order';
import useResolution from 'src/hooks/useResolution';
import RoleChip from 'src/components/chips/RoleChip';
import {
  SearchKeyword,
  SearchKeywordFilterGroup
} from 'src/components/filters';
import useUserInfo from 'src/hooks/user/use-user-info';
import { TaxCertificateOptionsDropdown } from './TaxCertificateOptionsDropdown';

const { actions: purchaseOrderActions } = slices.purchaseOrder;

const {
  actions: ecomOrderActions,
  selectors: ecomOrderSelectors
} = slices.ecomOrder;

const { actions: supplierActions } = slices.supplier;

interface Props {
  className?: string;
}

const useStyles = makeStyles((theme) => ({
  root: {},
  importButton: {
    marginRight: theme.spacing(1)
  },
  exportButton: {
    marginRight: theme.spacing(1)
  },
  cardContent: {
    flexDirection: 'row'
  }
}));

const Component = ({ className, ...rest }: Props) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const snackBar = useSnackBar();
  const { isMobile } = useResolution();
  const { isAccountingStaff } = useUserInfo();

  const { canCreatePurchaseOrder } = usePermissions();

  const [branchId, setBranchId] = useState<number>();

  const [category_id, setCategoryId] = useState<number>();
  const [clearCategory, setClearCategory] = useState<boolean>(false);
  const [clearFor2307, setClearFor2307] = useState<boolean>(false);
  const [createdByInput, setCreatedByInput] = useState<string>();
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const { selectedBranchIDs, setSelectedBranchIDs } = useBranchInfo();
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [selectedSupplierId, setSelectedSupplierId] = useState<number | null>(
    null
  );
  const [status, setStatus] = useState<PurchaseOrderStatusOption | null>(null);
  const [poKeyword, setPoKeyword] = useState<string>('');
  const [productKeyword, setProductKeyword] = useState<string>('');
  const [searchLabel, setSearchLabel] = useState<string | undefined>(
    searchFilter[0]
  );
  const [selectedFor2307, setSelectedFor2307] = useState<string | null>(null);
  const [isClearSearch, setIsClearSearch] = useState<boolean>(false);

  const ecomOrdersListRequest = useAppSelector(
    ecomOrderSelectors.getEcomOrderListRequest
  );

  const isLoading = useAppSelector(ecomOrderSelectors.selectEcomOrdersLoading);

  const hasStatusFilter = useMemo(
    () => (ecomOrdersListRequest?.status ? true : false),
    [ecomOrdersListRequest]
  );

  const getSuppliers = useCallback(async () => {
    const getSuppliersResponse = unwrapResult(
      await dispatch(
        supplierActions.getSuppliersThunk({ keyword: '', limit: 999999 })
      )
    );

    if (getSuppliersResponse?.success) {
      setSuppliers(getSuppliersResponse?.originalData.data || []);
    }
  }, [dispatch]);

  // mark onSearchOrders
  const onSearchOrders = debounce(() => {
    const payload: GetPurchaseOrdersPayload = {
      page: 1,
      category: category_id,
      created_by: createdByInput || null,
      po_date_from: fromDate ? fromDate.toISOString().split('T')[0] : null,
      po_date_to: toDate ? toDate.toISOString().split('T')[0] : null,
      branch: branchId,
      status: status?.value, // Use the value of the status option
      supplier: selectedSupplierId,
      ...(searchLabel === 'PO No.' && poKeyword ? { po_no: poKeyword } : {}),
      ...(searchLabel === 'Product' && productKeyword
        ? { product_name: productKeyword }
        : {}),
      for_tax_certificate: selectedFor2307
    };

    isAccountingStaff
      ? dispatch(purchaseOrderActions.getPurchaseOrderComputationThunk(payload))
      : dispatch(purchaseOrderActions.getPurchaseOrdersThunk(payload));

    dispatch(purchaseOrderActions.filterParamsAction(payload));
  }, 500);

  const clearFilters = () => {
    setFromDate(null);
    setToDate(null);
    setBranchId(undefined);
    setCategoryId(undefined);
    setCreatedByInput('');
    setIsClearSearch(true);
    setSelectedSupplierId(null);
    setStatus(null);
    setPoKeyword('');
    setClearCategory(true);
    setSelectedFor2307(null);
    dispatch(
      ecomOrderActions.updateGetEcomOrdersListRequest({
        keyword: ''
      })
    );

    setTimeout(() => setClearCategory(false), 0);
  };

  const handleChange = (event: CustomInputEvent) => {
    dispatch(
      ecomOrderActions.updateGetEcomOrdersListRequest({
        keyword: event?.target?.value
      })
    );
    setCreatedByInput(event?.target?.value);
  };

  const handlePOKeywordChange = (input: string | CustomInputEvent) => {
    const value = typeof input === 'string' ? input : input?.target?.value;

    switch (searchLabel) {
      case 'PO No':
        setPoKeyword(value);
        break;
      case 'Product':
        setProductKeyword(value);
        break;
      default:
        // Optional: set both or handle unknown label
        setPoKeyword(value);
        setProductKeyword(value);
        break;
    }
  };

  const onSearchClick = () => {
    onSearchOrders();
  };

  const onPressCreate = () => {
    navigate('/app/purchase-order/add');
  };

  const onHandleKeyPress = (e: CustomKeyboardEvent) => {
    if (e.key === 'Enter') {
      onSearchOrders();
    }
  };

  const onChangeFilterAction = (filter?: string) => {
    setSearchLabel(filter);
  };

  useEffect(() => {
    if (isClearSearch) {
      setIsClearSearch(false);
    }
  }, [isClearSearch]);

  //TODO: this shall thy automate fetch when category change
  // useEffect(() => {
  //   onSearchOrders();
  // }, [category_id]);

  useEffect(() => {
    if (isEmpty(suppliers)) {
      getSuppliers();
    }
  }, [getSuppliers, suppliers]);

  useEffect(() => {
    if (selectedBranchIDs?.length === 1) {
      setBranchId(selectedBranchIDs[0]);
    }
  }, [selectedBranchIDs]);

  useEffect(() => {
    if (isEmpty(selectedFor2307)) {
      setSelectedFor2307(null);
    }
  }, [selectedFor2307]);

  return (
    <div className={clsx(classes.root, className)} {...rest}>
      <Box
        display="flex"
        flexDirection={isMobile ? 'column' : 'row'}
        justifyContent="space-between"
      >
        <Box
          display="flex"
          flexDirection={isMobile ? 'column' : 'row'}
          alignItems={isMobile ? 'flex-start' : 'center'}
        >
          <Typography
            color="textPrimary"
            gutterBottom
            variant="h3"
            style={{ marginTop: 6 }}
          >
            Purchase Orders
          </Typography>
          {!isLoading ? (
            <RoleChip roles={roles} roleOptions={purchaseOrderRoles} />
          ) : null}
        </Box>

        {canCreatePurchaseOrder ? (
          <Box
            mt={isMobile ? 2 : 0}
            style={{ alignSelf: isMobile ? 'flex-end' : 'auto' }}
          >
            <Button
              onClick={onPressCreate}
              color="primary"
              variant="outlined"
              startIcon={
                <img
                  src="/static/images/icons/supply.png"
                  alt="supply"
                  style={{ width: 20, height: 20 }}
                />
              }
            >
              NEW PURCHASE ORDER
            </Button>
          </Box>
        ) : null}
      </Box>
      <Card style={{ marginTop: 15, padding: '1.5rem' }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={12}>
            <SearchKeywordFilterGroup
              filterOptions={searchFilter}
              onHandleFilterSearhBy={(filter?: string) => {
                setIsClearSearch(true);
                onChangeFilterAction(filter);
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <SearchKeyword
              isClearEvent={isClearSearch}
              searchLabel={`Search By ${searchLabel}`}
              onHandleSearchKeyword={(keyword: string) =>
                handlePOKeywordChange(keyword)
              }
              isDisable={isLoading}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <PurchaseOrderStatusField
              label="Status"
              isFilter={true}
              value={status?.value || null}
              onChange={(val) => setStatus(val)}
              size="medium"
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={2}
            style={{ display: 'flex', alignItems: 'center' }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={() => onSearchClick()}
              style={{ height: '100%' }}
              fullWidth
            >
              Apply Filter
            </Button>
          </Grid>
          <Grid
            item
            xs={12}
            md={2}
            style={{ display: 'flex', alignItems: 'center' }}
          >
            <Button
              variant="outlined"
              color="secondary"
              onClick={clearFilters}
              style={{ height: '100%' }}
              fullWidth
            >
              Clear Filters
            </Button>
          </Grid>
          <Grid item xs={12}>
            <Accordion
              style={{
                border: '1px solid rgba(0, 0, 0, 0.12)',
                borderRadius: 8,
                boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)'
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box width="100%" display="flex" justifyContent="center">
                  <Typography style={{ marginLeft: 40 }}>
                    MORE FILTER
                  </Typography>
                  <SettingsIcon style={{ marginLeft: 10 }} />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Grid
                      container
                      direction="column"
                      justifyContent="center"
                      alignItems="center"
                    >
                      <Typography variant="h5" style={{ marginBottom: 10 }}>
                        PO Date Range
                      </Typography>
                      <DatePickerRangeComponent
                        fromDateMax={dateToday()}
                        fromDateValue={fromDate}
                        toDateValue={toDate}
                        dateToLabel="End Date"
                        dateFromLabel="Start Date"
                        toDateMin={fromDate ? fromDate.toISOString() : null}
                        onChangeToDate={(date) => setToDate(date)}
                        onChangeFromDate={(date) => setFromDate(date)}
                      />
                    </Grid>
                  </Grid>

                  <Grid item xs={12}>
                    <Divider />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      value={ecomOrdersListRequest?.keyword || ''}
                      onChange={handleChange}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SvgIcon fontSize="small" color="action">
                              <SearchIcon />
                            </SvgIcon>
                          </InputAdornment>
                        )
                      }}
                      placeholder="Search Created By"
                      variant="outlined"
                      onKeyPress={onHandleKeyPress}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    {multiBranchFeat ? (
                      <BranchListDropDown
                        defaultValue={branchId ? [branchId] : []}
                        isElevated={false}
                        paperSpace={0}
                        multiple={false}
                        onHandleBranchChange={(branchIds?: number[]) => {
                          if (branchIds && branchIds.length > 0) {
                            setBranchId(branchIds[0]);
                          }
                        }}
                        allowNoSelectedBranch={false}
                      />
                    ) : null}
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      id="supplier"
                      options={suppliers || []}
                      getOptionLabel={(option) => option?.name || ''}
                      value={
                        suppliers.find(
                          (supplier) => supplier.id === selectedSupplierId
                        ) || null
                      }
                      onChange={(e: any, val) =>
                        setSelectedSupplierId(val?.id || null)
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          name="supplier"
                          label="Supplier"
                          variant="outlined"
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <CategoryListDropdown
                      onHandleCategories={(category_id) => {
                        setCategoryId(category_id);
                      }}
                      clearCategory={clearCategory}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TaxCertificateOptionsDropdown
                      value={selectedFor2307}
                      onChange={(val) => setSelectedFor2307(val || '')}
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Grid>
        </Grid>
      </Card>
      {isLoading ? <LinearProgress /> : null}
    </div>
  );
};

export const EcomOrdersListToolbar = React.memo(Component);
